import type { CredentialSetPayload, CredentialUpdatePayload } from "./api-client.js";

export type CredentialKind = "api_key" | "oauth";
export type CredentialNoticeTone = "success" | "warning" | "danger";

export interface CredentialFormValues {
  kind: CredentialKind;
  displayName: string;
  namespace: string;
  key: string;
  description: string;
  value: string;
}

export type CredentialFormErrors = Partial<Record<keyof CredentialFormValues, string>>;

export interface CredentialNotice {
  tone: CredentialNoticeTone;
  message: string;
}

const CREDENTIAL_SEGMENT_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

export function emptyCredentialFormValues(kind: CredentialKind = "api_key"): CredentialFormValues {
  return {
    kind,
    displayName: "",
    namespace: kind === "api_key" ? "custom" : "",
    key: "",
    description: "",
    value: "",
  };
}

export function credentialSlugFromName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
  return slug || "credential";
}

export function credentialReference(namespace: string, key: string): string {
  const trimmedNamespace = namespace.trim();
  const trimmedKey = key.trim();
  return trimmedNamespace && trimmedKey ? `${trimmedNamespace}.${trimmedKey}` : "";
}

export function validateCredentialForm(
  values: CredentialFormValues,
  mode: "create" | "edit" = "create",
): { valid: boolean; errors: CredentialFormErrors } {
  const errors: CredentialFormErrors = {};
  if (!values.displayName.trim()) errors.displayName = "Name is required";
  if (!isCredentialSegment(values.namespace)) {
    errors.namespace = "Use lowercase letters, numbers, underscores, or hyphens";
  }
  if (!isCredentialSegment(values.key)) {
    errors.key = "Use lowercase letters, numbers, underscores, or hyphens";
  }
  if (!values.description.trim()) errors.description = "Description is required";
  if (values.kind === "api_key" && mode === "create" && !values.value.trim()) {
    errors.value = "Value is required";
  }
  return { valid: Object.keys(errors).length === 0, errors };
}

export function credentialFormToSetPayload(values: CredentialFormValues): CredentialSetPayload {
  if (values.kind !== "api_key") {
    throw new Error("OAuth credentials are created through the provider connect flow");
  }
  return {
    type: "api_key",
    displayName: values.displayName.trim(),
    namespace: values.namespace.trim(),
    key: values.key.trim(),
    description: values.description.trim(),
    value: values.value.trim(),
  };
}

export function credentialFormToUpdatePayload(
  values: CredentialFormValues,
): Omit<CredentialUpdatePayload, "namespace" | "key"> {
  const value = values.value.trim();
  return {
    displayName: values.displayName.trim(),
    description: values.description.trim(),
    ...(value ? { value } : {}),
  };
}

export function parseCredentialOAuthResult(search: string): CredentialNotice | undefined {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const connected = params.get("connected");
  if (connected) {
    return { tone: "success", message: `Connected ${connected}.` };
  }

  // The MCP/connector OAuth callback page (`OAuthCallbackPage`) navigates back
  // to the connectors tab with `?oauth_success=<serverId>` on success.
  const oauthSuccess = params.get("oauth_success");
  if (oauthSuccess) {
    return { tone: "success", message: `Connected ${oauthSuccess}.` };
  }

  const error = params.get("oauth_error");
  if (!error) return undefined;
  const provider = params.get("provider") ?? "Provider";
  switch (error) {
    case "cancelled":
      return { tone: "warning", message: "Connection cancelled." };
    case "denied":
      return {
        tone: "danger",
        message: `${provider} didn't grant access. Check permissions and try again.`,
      };
    case "invalid_state":
      return { tone: "danger", message: "Something went wrong. Please try connecting again." };
    case "exchange_failed":
      return { tone: "danger", message: `Couldn't connect to ${provider}. Please try again.` };
    case "timeout":
      return { tone: "warning", message: "Connection timed out. Please try again." };
    case "gateway_offline":
      return {
        tone: "warning",
        message: "Couldn't connect to specialists. Try again when the connection returns.",
      };
    case "vault_write_failed":
      return {
        tone: "danger",
        message: "Couldn't save the connection. Try again.",
      };
    case "credential_conflict":
      return { tone: "danger", message: "A credential with that reference already exists." };
    case "oauth_discovery_failed":
      return {
        tone: "danger",
        message: `${provider} does not currently support this connection.`,
      };
    case "oauth_client_not_configured":
      return {
        tone: "danger",
        message: `${provider} needs administrator setup before it can be connected.`,
      };
    case "oauth_begin_failed":
      return {
        tone: "danger",
        message: `Couldn't start the connection to ${provider}. Try again.`,
      };
    case "composio_api_key_invalid":
      return {
        tone: "danger",
        message: `Couldn't connect ${provider} — it isn't enabled for your environment.`,
      };
    case "composio_missing_fields":
      return {
        tone: "danger",
        message: `${provider} needs additional setup before it can be connected. Contact your administrator.`,
      };
    case "composio_upstream_error":
      return {
        tone: "danger",
        message: `Couldn't connect to ${provider}. Try again.`,
      };
    default:
      return { tone: "danger", message: "Couldn't connect. Please try again." };
  }
}

function isCredentialSegment(value: string): boolean {
  return CREDENTIAL_SEGMENT_PATTERN.test(value.trim());
}
